import InfoPopup from './InfoPopup';

/**
 * InfoIconManager - Manages all info icons and their popups
 * Handles creation, positioning, and lifecycle of info tooltips
 */
export default class InfoIconManager {
    /**
     * Create a new InfoIconManager
     * 
     * @param {HTMLElement} container - Container element for steps
     * @param {object} stepController - StepController instance for accessing handlers
     */
    constructor(container, stepController) {
        this.container = container;
        this.stepController = stepController;
        this.popups = new Map(); // stepId => InfoPopup
        
        this.initialize();
    }
    
    /**
     * Initialize the manager and bind events
     */
    initialize() {
        // Listen for side changes to recreate icons
        this.container.addEventListener('stc:steps:sideChange', this.handleSideChange.bind(this));
        
        // Initial creation
        this.createPopups();
        
        // Listen for dynamic icon creation
        this.container.addEventListener('stc:steps:stepRendered', this.handleStepRendered.bind(this));
    }
    
    /**
     * Create popups for all info icons in the container
     */
    createPopups() {
        // Find all info icons
        const infoIcons = this.container.querySelectorAll('.stc-step-info-icon');
        
        // Create popups for each
        infoIcons.forEach(icon => {
            const stepId = icon.dataset.stepId;
            if (!stepId) return;
            
            this.createPopupForStep(stepId, icon);
        });
    }
    
    /**
     * Create a popup for a specific step
     * 
     * @param {string} stepId - Step ID
     * @param {HTMLElement} icon - Icon element
     */
    createPopupForStep(stepId, icon) {
        // Check if popup already exists
        if (this.popups.has(stepId)) {
            // Update existing popup
            const popup = this.popups.get(stepId);
            popup.targetElement = icon;
            return;
        }
        
        // Get info content from step handler
        const handler = this.stepController.getHandler(stepId);
        if (!handler) return;
        
        const infoContent = handler.getInfoContent();
        if (!infoContent) return;
        
        // Create popup
        const popup = new InfoPopup(icon, {
            title: infoContent.title,
            description: infoContent.description,
            imageSrc: infoContent.imageSrc,
            position: infoContent.position || 'right',
            showOnHover: true,
            closeOnClickOutside: true
        });
        
        // Store popup
        this.popups.set(stepId, popup);
    }
    
    /**
     * Handle side change event
     * 
     * @param {Event} e - Side change event
     */
    handleSideChange(e) {
        // Clean up old popups
        this.disposePopups();
        
        // Wait for new steps to render
        setTimeout(() => {
            this.createPopups();
        }, 100);
    }
    
    /**
     * Handle step rendered event
     * 
     * @param {Event} e - Step rendered event
     */
    handleStepRendered(e) {
        const { detail } = e;
        if (!detail || !detail.stepId) return;
        
        // Find icon for this step
        const icon = this.container.querySelector(`.stc-step-info-icon[data-step-id="${detail.stepId}"]`);
        if (!icon) return;
        
        // Create or update popup
        this.createPopupForStep(detail.stepId, icon);
    }
    
    /**
     * Dispose all popups
     */
    disposePopups() {
        this.popups.forEach(popup => popup.dispose());
        this.popups.clear();
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Remove event listeners
        this.container.removeEventListener('stc:steps:sideChange', this.handleSideChange.bind(this));
        this.container.removeEventListener('stc:steps:stepRendered', this.handleStepRendered.bind(this));
        
        // Dispose all popups
        this.disposePopups();
    }
}